import motion from '@lib/motion';
import { ResizeManager } from '@theme/event-manager';
import {
  throttle,
  debounce,
  motionReduced,
  lockScroll,
  unlockScroll,
  layoutRecalcManager,
  layoutPhaseMeasure,
  layoutPhaseApply,
  createSlider,
} from '@theme/theme-global';

class CustomHeader extends HTMLElement {
  constructor() {
    super();
    this.setMenusBounds = this.setMenusBounds.bind(this);
    this._onLayoutRecalc = this._onLayoutRecalc.bind(this);
    this._cachedHeaderHeight = null;
    this._lastHeaderHeight = null;
    this._lastStickyHeaderHeight = null;
    this._onUpdateHeight = () => this.setHeight();
    this._scheduleHeightFromResize = debounce(() => layoutRecalcManager.request(this._onLayoutRecalc), 150);
  }

  get allowTransparent() {
    return this.getAttribute('data-header-transparent') === 'true';
  }

  get headerSection() {
    return document.querySelector('.header-main');
  }

  connectedCallback() {
    this.init();
    layoutRecalcManager.register(this.setMenusBounds);
    layoutRecalcManager.register(this._onLayoutRecalc);

    if (window.ResizeObserver) {
      this._headerResizeObserver = new ResizeObserver(this._scheduleHeightFromResize);
      this._headerResizeObserver.observe(this);
    }

    document.addEventListener('header:updateHeight', this._onUpdateHeight);

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  disconnectedCallback() {
    layoutRecalcManager.unregister(this.setMenusBounds);
    layoutRecalcManager.unregister(this._onLayoutRecalc);
    this._headerResizeObserver?.disconnect();
    document.removeEventListener('header:updateHeight', this._onUpdateHeight);
  }

  init() {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile && typeof window.skyAfterLCP === 'function') {
      // Avoid measuring header height during the critical rendering window.
      // Liquid already provides --headerHeightInitial for mobile so layout remains stable.
      window.skyAfterLCP(() => layoutRecalcManager.request(this._onLayoutRecalc));
      return;
    }

    layoutRecalcManager.request(this._onLayoutRecalc);
  }

  _onLayoutRecalc(phase) {
    if (layoutPhaseMeasure(phase)) {
      this._pendingHeight = this.getBoundingClientRect().height;
    }

    if (layoutPhaseApply(phase)) {
      if (this._pendingHeight == null) return;
      this._applyHeaderHeight(this._pendingHeight);
    }
  }

  _hasOpenMenu() {
    return Boolean(
      this.querySelector('details[is="details-dropdown"][open], details[is="details-mega"][open]'),
    );
  }

  _applyHeaderHeight(measured) {
    this._cachedHeaderHeight = measured;
    const nextHeight = `${measured.toFixed(1)}px`;

    if (nextHeight === this._lastHeaderHeight) return false;

    this._lastHeaderHeight = nextHeight;
    // Measured height for sticky UI. Do not overwrite --headerHeightInitial —
    // hero padding reads that so boot-time measure cannot CLS the banner.
    document.body.style.setProperty('--headerHeight', nextHeight);
    document.body.style.setProperty('--headerHeightMeasured', nextHeight);
    return true;
  }

  _setStickyHeaderHeight(nextHeight) {
    if (nextHeight === this._lastStickyHeaderHeight) return;

    this._lastStickyHeaderHeight = nextHeight;
    document.body.style.setProperty('--stickyHeaderHeight', nextHeight);
  }

  _stickyHeightFromCache() {
    if (this._cachedHeaderHeight != null) {
      return `${this._cachedHeaderHeight.toFixed(1)}px`;
    }

    const measured = this.getBoundingClientRect().height;
    this._applyHeaderHeight(measured);
    return `${measured.toFixed(1)}px`;
  }

  setHeight() {
    const measured = this.getBoundingClientRect().height;
    this._applyHeaderHeight(measured);

    if (this._hasOpenMenu()) {
      this.setMenusBounds();
    }
  }

  setMenusBounds(phase) {
    if (!this._hasOpenMenu()) {
      if (layoutPhaseMeasure(phase)) this._menuBoundsCache = null;
      return;
    }

    if (layoutPhaseMeasure(phase)) {
      this._menuBoundsCache = [];
      this.querySelectorAll('details[is="details-dropdown"], details[is="details-mega"]').forEach((details) => {
        const { left, top, width } = details.getBoundingClientRect();
        this._menuBoundsCache.push({ details, left, top, width });
      });
    }

    if (layoutPhaseApply(phase)) {
      (this._menuBoundsCache || []).forEach(({ details, left, top, width }) => {
        details.style.setProperty('--extraLeft', `${left.toFixed(1)}px`);
        details.style.setProperty('--extraTop', `${top.toFixed(1)}px`);
        details.style.setProperty('--extraWidth', `${width.toFixed(1)}px`);
      });
    }
  }
}
customElements.define('custom-header', CustomHeader, { extends: 'header' });

class StickyHeader extends CustomHeader {
  constructor() {
    super();
    this.isScrolled = false;
    this.buffer = 24;
    this.scrolledAddAt = 0;
    this.scrolledRemoveAt = 0;
    this.hiddenAddAt = 0;
    this._userScrollActive = false;
    this._userScrollIdleTimer = null;
    this._markUserScroll = this._markUserScroll.bind(this);
  }
  get isAlwaysSticky() {
    return this.getAttribute('data-sticky-type') === 'always';
  }
  get headerSection() {
    return document.querySelector('.header-main');
  }
  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.beforeInit();
  }
  disconnectedCallback() {
    clearTimeout(this._userScrollIdleTimer);
    window.removeEventListener('scroll', this._onScroll, false);
    window.removeEventListener('resize', this._onResize, false);
    window.removeEventListener('wheel', this._markUserScroll);
    window.removeEventListener('touchmove', this._markUserScroll);
    super.disconnectedCallback();
  }
  updateThresholds() {
    const scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    if (scrollable === this._lastScrollable) return;
    this._lastScrollable = scrollable;
    const idealAdd = 500;
    const maxAdd = 560;

    let scrolledAdd = idealAdd;
    if (scrollable > 0 && scrollable < idealAdd) {
      scrolledAdd = scrollable * 0.5;
    } else if (scrollable >= idealAdd) {
      // Normal/long pages: stay in ~450–560 band
      scrolledAdd = Math.min(maxAdd, Math.max(450, Math.min(idealAdd, scrollable * 0.1)));
    }

    this.scrolledAddAt = scrolledAdd;
    // Remove near the same scroll position as add (small buffer prevents flicker)
    this.scrolledRemoveAt = Math.max(scrolledAdd - this.buffer, this.buffer);

    // Larger gap after scrolled before hide — scales with page scrollable height
    // Short pages: ~25% of scrollable; typical: ~12–18% capped so it stays noticeable
    let hiddenExtra;
    if (scrollable <= 0) {
      hiddenExtra = 320;
    } else if (scrollable < 1200) {
      hiddenExtra = Math.max(200, scrollable * 0.28);
    } else {
      hiddenExtra = Math.min(480, Math.max(280, scrollable * 0.1, window.innerHeight * 0.28));
    }
    const desiredHidden = scrolledAdd + hiddenExtra;
    this.hiddenAddAt =
      scrollable > 0 ? Math.min(desiredHidden, Math.max(scrolledAdd + this.buffer, scrollable - 20)) : desiredHidden;
  }
  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.setAttribute('data-sticky-type', this.getAttribute('data-sticky-type'));

    if (this.isAlwaysSticky) {
      this.headerSection.classList.add('header-sticky');
    }

    this.scrolledAddAt = 500;
    this.scrolledRemoveAt = 476;
    this.hiddenAddAt = 820;
    this._thresholdsReady = false;

    this._onScroll = throttle(() => {
      if (!this._thresholdsReady) {
        this.updateThresholds();
        this._thresholdsReady = true;
      }
      this.onScrollHandler();
    });
    this._onResize = throttle(() => {
      this.updateThresholds();
      this._thresholdsReady = true;
      this.setHeight();
      this.onScrollHandler();
    });

    window.addEventListener('scroll', this._onScroll, false);
    window.addEventListener('resize', this._onResize, false);
    window.addEventListener('wheel', this._markUserScroll, { passive: true });
    window.addEventListener('touchmove', this._markUserScroll, { passive: true });

    const idleThresholds = () => {
      if (this._thresholdsReady) return;
      this.updateThresholds();
      this._thresholdsReady = true;
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(idleThresholds, { timeout: 2500 });
    } else if (document.readyState === 'complete') {
      setTimeout(idleThresholds, 0);
    } else {
      window.addEventListener('load', () => setTimeout(idleThresholds, 0), { once: true });
    }
  }

  _markUserScroll() {
    this._userScrollActive = true;
    clearTimeout(this._userScrollIdleTimer);
    this._userScrollIdleTimer = setTimeout(() => {
      this._userScrollActive = false;
    }, 180);
  }

  _shouldCloseMenusOnScroll(scrollTop) {
    if (!this._userScrollActive) return false;
    if (!(this.isScrolled && scrollTop > this.scrolledAddAt)) return false;
    if (this.headerSection?.matches(':hover')) return false;
    return true;
  }

  _refreshHeightAfterClassChange() {
    const measured = this.getBoundingClientRect().height;
    this._applyHeaderHeight(measured);
    this._setStickyHeaderHeight(`${measured.toFixed(1)}px`);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    // Toggle 'header-scrolled': add ~850px band, remove earlier while scrolling up
    if (!this.isScrolled && scrollTop > this.scrolledAddAt) {
      this.isScrolled = true;
      this.headerSection.classList.add('header-scrolled');
      this._refreshHeightAfterClassChange();
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: true } }));
    } else if (this.isScrolled && scrollTop < this.scrolledRemoveAt) {
      this.isScrolled = false;
      this.headerSection.classList.remove('header-scrolled');
      this._refreshHeightAfterClassChange();
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: false } }));
    }

    // Close menus only during active wheel/touch — not Lenis residual easing after the user stopped.
    if (this._shouldCloseMenusOnScroll(scrollTop)) {
      this.headerSection.querySelectorAll('details').forEach((details) => {
        if (details.hasAttribute('open')) {
          details.open = false;
        }
      });
    }

    // Hide only for on-scroll-up. Always-sticky must stay visible and clickable.
    // Do not gate this on _userScrollActive — wheel events are bursty and Lenis keeps
    // scrolling between them, which would add/remove header-hidden and flicker.
    if (!this.isAlwaysSticky && scrollTop > this.hiddenAddAt) {
      if (scrollTop > this.currentScrollTop) {
        this.headerSection.classList.add('header-hidden');
        this._setStickyHeaderHeight('0px');
      } else {
        this.headerSection.classList.remove('header-hidden');
        this._setStickyHeaderHeight(this._stickyHeightFromCache());
      }
    } else {
      this.headerSection.classList.remove('header-hidden');
      this._setStickyHeaderHeight(this._stickyHeightFromCache());
    }

    this.currentScrollTop = scrollTop;
  }
}
customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const dropdownScrollLockCountMap = new WeakMap();
const HOVER_OPEN_DELAY = 80;
const HOVER_CLOSE_DELAY = 180;
const fineHoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

function prefersFineHover() {
  return fineHoverQuery.matches;
}

class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.cssClasses = {
      bodyClass: 'has-dropdown-menu',
    };

    this.eventNames = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow',
    };

    this._isOpen = this.hasAttribute('open');
    this._visible = false;
    this._pointerInside = false;
    this._lastPointerType = 'mouse';
    this._transitionToken = 0;
    this._contentOpenTimer = null;
    this._tabsLoadTimer = null;
    this._outsideBindTimer = null;
    this._animations = new Set();
    this.hoverTimeout = null;

    this.handleClickOutside = this.detectClickOutside.bind(this);
    this.handleEscKey = this.detectEscKeyboard.bind(this);
    this.handleFocusOut = this.detectFocusOut.bind(this);
    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.handleSummaryFocusIn = this.handleSummaryFocusIn.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);

    this.summaryElement.addEventListener('pointerdown', this.handlePointerDown);
    this.summaryElement.addEventListener('click', this.handleSummaryClick, true);
    this.summaryElement.addEventListener('focusin', this.handleSummaryFocusIn);
    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointerleave', this.handlePointerLeave);
  }

  connectedCallback() {
    if (!Shopify.designMode || DetailsDropdown._editorBound) return;
    DetailsDropdown._editorBound = true;

    ['shopify:section:load', 'shopify:section:unload', 'shopify:block:deselect'].forEach((event) => {
      document.addEventListener(event, DetailsDropdown.onEditorChange);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.hoverTimeout);
    clearTimeout(this._contentOpenTimer);
    clearTimeout(this._tabsLoadTimer);
    clearTimeout(this._outsideBindTimer);
    this.cancelAnimations();
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keyup', this.handleEscKey);
    document.removeEventListener('focusout', this.handleFocusOut);

    if (this._scrollLockActive) {
      this._scrollLockActive = false;
      const lockCount = Math.max(0, dropdownScrollLockCountMap.get(DetailsDropdown) - 1);
      dropdownScrollLockCountMap.set(DetailsDropdown, lockCount);
    }

    requestAnimationFrame(() => DetailsDropdown.syncBodyState());
  }

  cancelAnimations() {
    this._animations.forEach((animation) => animation.cancel?.());
    this._animations.clear();
  }

  trackAnimation(animation) {
    this._animations.add(animation);
    animation.finished.then(
      () => this._animations.delete(animation),
      () => this._animations.delete(animation),
    );
    return animation;
  }

  static onEditorChange(event) {
    const section = event.target.closest?.('.shopify-section') ?? event.target;
    if (!section?.classList?.contains('header-main')) return;
    requestAnimationFrame(() => DetailsDropdown.syncBodyState());
  }

  static syncBodyState() {
    const hasOpen = document.querySelector(
      '.header-main details[is="details-dropdown"][open], .header-main details[is="details-mega"][open]',
    );

    document.body.classList.toggle('has-dropdown-menu', !!hasOpen);
    document.body.classList.toggle(
      'with-mega',
      !!document.querySelector('.header-main details[is="details-mega"][open]'),
    );

    if (!hasOpen && dropdownScrollLockCountMap.get(DetailsDropdown) > 0) {
      dropdownScrollLockCountMap.set(DetailsDropdown, 0);
      unlockScroll();
    }
  }

  set open(value) {
    const next = Boolean(value);
    if (next === this._isOpen) return;

    this._isOpen = next;

    if (this.isConnected) {
      this.handleTransition(next);
    } else if (next) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }

  get open() {
    return this._isOpen;
  }

  get summaryElement() {
    return this.firstElementChild;
  }

  get contentElement() {
    return this.lastElementChild;
  }

  get trigger() {
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    return this.getAttribute('level') || 'top';
  }

  get shouldHover() {
    return this.trigger === 'hover' && prefersFineHover();
  }

  get closedTranslateY() {
    return this.level === 'top' ? '-105%' : '2rem';
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  handlePointerDown(event) {
    this._lastPointerType = event.pointerType || 'mouse';
  }

  handleSummaryClick(event) {
    event.preventDefault();

    const link = this.summaryElement.getAttribute('data-link') || '';
    const mouseHoverClick = this.shouldHover && this._lastPointerType !== 'touch';

    if (mouseHoverClick && link) {
      window.location.href = link;
      return;
    }

    this.open = !this.open;
  }

  handleSummaryFocusIn(event) {
    if (event.target !== this.summaryElement) return;
    if (!this.shouldHover) return;
    if (this._pointerInside) return;
    this.open = true;
  }

  handlePointerEnter(event) {
    if (event.pointerType === 'touch' || !this.shouldHover) return;

    this._pointerInside = true;
    clearTimeout(this.hoverTimeout);

    if (this.open) return;

    this.hoverTimeout = setTimeout(() => {
      this.hoverTimeout = null;
      if (this._pointerInside) this.open = true;
    }, HOVER_OPEN_DELAY);
  }

  handlePointerLeave(event) {
    if (event.pointerType === 'touch' || !this.shouldHover) return;

    this._pointerInside = false;
    clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.hoverTimeout = null;
      if (!this._pointerInside) this.open = false;
    }, HOVER_CLOSE_DELAY);
  }

  closeOtherMenus() {
    document.querySelectorAll('.header-main details[is="details-dropdown"], .header-main details[is="details-mega"]').forEach((details) => {
      if (details === this) return;
      if (this.contains(details) || details.contains(this)) return;
      if (details.open) details.open = false;
    });
  }

  bindOutsideListeners() {
    clearTimeout(this._outsideBindTimer);
    this._outsideBindTimer = setTimeout(() => {
      this._outsideBindTimer = null;
      if (!this.open) return;
      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('keyup', this.handleEscKey);
      document.addEventListener('focusout', this.handleFocusOut);
    }, 0);
  }

  unbindOutsideListeners() {
    clearTimeout(this._outsideBindTimer);
    this._outsideBindTimer = null;
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keyup', this.handleEscKey);
    document.removeEventListener('focusout', this.handleFocusOut);
  }

  resetClosedVisuals() {
    this.cancelAnimations();
    this.contentElement?.style.removeProperty('opacity');
    this.contentElement?.style.removeProperty('visibility');
    this.contentElement?.firstElementChild?.style.removeProperty('transform');
    this.contentElement?.firstElementChild?.style.removeProperty('visibility');
  }

  async handleTransition(isOpening) {
    const token = ++this._transitionToken;

    if (isOpening) {
      this.closeOtherMenus();
      clearTimeout(this._contentOpenTimer);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      this.contentElement.setAttribute('open', '');
      DetailsDropdown.syncBodyState();

      this.bindOutsideListeners();
      this.checkReverseLayout();
      this.setMenusBounds();

      await this.animateIn();

      if (token !== this._transitionToken || !this.open) return;

      this._visible = true;
      this._scrollLockActive = true;
      const lockCount = dropdownScrollLockCountMap.get(DetailsDropdown) + 1;
      dropdownScrollLockCountMap.set(DetailsDropdown, lockCount);
      if (lockCount === 1) lockScroll();

      this.dispatchEvent(new CustomEvent(this.eventNames.afterShow));
      return;
    }

    const shouldAnimateOut = this._visible;
    this._visible = false;
    clearTimeout(this._contentOpenTimer);
    this._contentOpenTimer = null;
    clearTimeout(this._tabsLoadTimer);
    this._tabsLoadTimer = null;
    this.summaryElement.removeAttribute('open');
    this.contentElement.removeAttribute('open');
    this.unbindOutsideListeners();

    if (shouldAnimateOut) {
      await this.animateOut();
    } else {
      this.resetClosedVisuals();
    }

    if (token !== this._transitionToken) return;

    if (this._scrollLockActive) {
      this._scrollLockActive = false;
      const lockCount = Math.max(0, dropdownScrollLockCountMap.get(DetailsDropdown) - 1);
      dropdownScrollLockCountMap.set(DetailsDropdown, lockCount);
      if (lockCount === 0) unlockScroll();
    }

    if (!this.open) this.removeAttribute('open');

    DetailsDropdown.syncBodyState();

    this.dispatchEvent(new CustomEvent(this.eventNames.afterHide));
  }

  async animateIn() {
    this.cancelAnimations();

    this.trackAnimation(
      motion.animate(
        this.contentElement,
        { opacity: [0, 1], visibility: 'visible' },
        { duration: motionReduced() ? 0 : 0.6, easing: [0.7, 0, 0.2, 1], delay: motionReduced() ? 0 : 0.2 },
      ),
    );

    const animation = this.trackAnimation(
      motion.animate(
        this.contentElement.firstElementChild,
        { transform: [`translateY(${this.closedTranslateY})`, 'translateY(0)'] },
        { duration: motionReduced() ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] },
      ),
    );

    return animation.finished.catch(() => { });
  }

  async animateOut() {
    this.cancelAnimations();

    this.trackAnimation(
      motion.animate(
        this.contentElement,
        { opacity: 0, visibility: 'hidden' },
        { duration: motionReduced() ? 0 : 0.3, easing: [0.7, 0, 0.2, 1] },
      ),
    );

    const animation = this.trackAnimation(
      motion.animate(
        this.contentElement.firstElementChild,
        { transform: `translateY(${this.closedTranslateY})` },
        { duration: motionReduced() ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] },
      ),
    );

    return animation.finished.catch(() => { });
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const openDropdown = event.target.closest('details[open]');
      if (openDropdown) {
        openDropdown.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  checkReverseLayout() {
    this.contentElement.classList.remove('should-reverse');
    const rect = this.contentElement.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }

  setMenusBounds() {
    const { left, top, width } = this.getBoundingClientRect();

    this.style.setProperty('--extraLeft', `${left.toFixed(1)}px`);
    this.style.setProperty('--extraTop', `${top.toFixed(1)}px`);
    this.style.setProperty('--extraWidth', `${width.toFixed(1)}px`);
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });

dropdownScrollLockCountMap.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => (this.open = true));
      this.addEventListener('shopify:block:deselect', () => (this.open = false));
    }
  }

  async animateIn() {
    this.cancelAnimations();
    clearTimeout(this._tabsLoadTimer);

    this.contentElement.querySelector('tabs-element')?.unload();
    this._tabsLoadTimer = setTimeout(
      () => {
        this._tabsLoadTimer = null;
        if (!this.open) return;
        this.contentElement.querySelector('tabs-element')?.load();
      },
      motionReduced() ? 0 : 450,
    );

    document.body.classList.add('with-mega');
    const animation = this.trackAnimation(
      motion.animate(
        this.contentElement.firstElementChild,
        { visibility: 'visible', transform: ['translateY(-105%)', 'translateY(0)'] },
        { duration: motionReduced() ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] },
      ),
    ).finished;

    return animation.catch(() => { });
  }

  async animateOut() {
    this.cancelAnimations();
    clearTimeout(this._tabsLoadTimer);
    this._tabsLoadTimer = null;
    document.body.classList.remove('with-mega');
    const animation = this.trackAnimation(
      motion.animate(
        this.contentElement.firstElementChild,
        { visibility: 'hidden', transform: 'translateY(-105%)' },
        { duration: motionReduced() ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] },
      ),
    ).finished;

    return animation.catch(() => { });
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' });

if (!customElements.get('menu-tab-header')) {
  class MenuTabHeader extends HTMLAnchorElement {
    constructor() {
      super();
      this.storageKey = 'activeMenuTab';
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));

      const savedIndex = localStorage.getItem(this.storageKey);

      if (savedIndex) {
        this.updateAllHeaders(savedIndex);
      } else {
        const firstTab = document.querySelector('[is="menu-tab-header"]');
        if (firstTab) {
          this.updateAllHeaders(firstTab.dataset.index);
        }
      }
    }

    handleClick(e) {
      const index = this.dataset.index;
      const hasLink = this.dataset.hasLink === 'true';

      localStorage.setItem(this.storageKey, index);

      if (hasLink) return;

      e.preventDefault();

      window.dispatchEvent(
        new CustomEvent('menuTabChange', {
          detail: { index },
        }),
      );

      this.updateAllHeaders(index);
    }

    handleKeydown(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    }

    updateAllHeaders(index) {
      document.querySelectorAll('[is="menu-tab-header"]').forEach((tab) => {
        const isActive = tab.dataset.index === index;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive);
      });
    }
  }
  customElements.define('menu-tab-header', MenuTabHeader, { extends: 'a' });
}

document.addEventListener('shopify:section:load', () => {
  const savedIndex = localStorage.getItem('activeMenuTab');

  if (savedIndex) {
    window.dispatchEvent(
      new CustomEvent('menuTabChange', {
        detail: { index: savedIndex },
      }),
    );
  }
});
